import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import { inr, dt as dayjs, apiError } from '../utils/format';
import CashCount, { countsTotal, countsSummary, countsDiff } from './CashCount';

// Opening and closing a billing register. The session belongs to the logged-in
// cashier at one branch: every bill they ring up is stamped with it, and closing
// counts the drawer against what those bills say should be in it.

export const OpenRegisterCard = ({ locationId, locName, userName, dayClosed, lastClose, onOpened }) => {
  const [counts, setCounts] = useState({});
  const [empty, setEmpty] = useState(false);   // an empty drawer is a deliberate answer
  const [note, setNote] = useState('');
  const [openingNote, setOpeningNote] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const openingCash = countsTotal(counts);
  const counted = Object.values(counts).some((v) => Number(v) > 0);

  // What the drawer should hold: whatever this branch's last close counted.
  const expected = lastClose ? Number(lastClose.countedCash) : null;
  const variance = expected == null ? 0 : Math.round((openingCash - expected) * 100) / 100;
  const mismatch = expected != null && (counted || empty) && Math.abs(variance) > 1;
  const denomDiff = mismatch ? countsDiff(counts, lastClose.counts) : '';

  const open = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/pos/session/open', {
        locationId: Number(locationId), openingCash, openingCounts: empty ? {} : counts,
        openingNote: openingNote.trim() || undefined, note,
      });
      toast.success(`Register open — ${data.session.code}`);
      onOpened(data.session);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="pos-closed">
      <div className="card card-pad">
        <span className={`badge ${dayClosed ? 'badge-red' : 'badge-gold'}`}>{dayClosed ? 'Day closed' : 'Register closed'}</span>
        <h2 style={{ margin: '.6rem 0 .2rem' }}>{dayClosed ? 'Billing is closed for today' : 'Open the register to start billing'}</h2>
        {!locationId ? (
          <p className="form-error">Select a location from the top bar first.</p>
        ) : dayClosed ? (
          <p className="muted">
            Billing is closed for the day at <strong>{locName}</strong>. A manager can reopen it from <Link to="/day-close">Day Close</Link>,
            after which you can open a fresh register.
          </p>
        ) : (
          <>
            <p className="muted">
              Billing at <strong>{locName}</strong> as <strong>{userName}</strong>. Count the cash already in the drawer and enter it —
              your closing count is measured against it, and every bill you ring up is recorded under your name.
            </p>
            {lastClose ? (
              <p className="muted small" style={{ margin: '.5rem 0 0' }}>
                Last closed {dayjs(lastClose.closedAt)}{lastClose.by ? ` by ${lastClose.by}` : ''}{lastClose.kind === 'day' ? ' (day close)' : ''} at
                {' '}<strong>{inr(lastClose.countedCash)}</strong>
                {lastClose.counts ? <span title={countsSummary(lastClose.counts)}> — hover for the breakdown</span> : null}
                {lastClose.banked ? ' · counted as banked' : ''}
              </p>
            ) : (
              <p className="muted small" style={{ margin: '.5rem 0 0' }}>No earlier close for this branch — this count sets the starting point.</p>
            )}
            <div style={{ marginTop: '.5rem' }}>
              <CashCount counts={counts} onChange={(c) => { setCounts(c); setEmpty(false); }} label="Open balance — count the drawer" />
            </div>
            {expected != null && (counted || empty) && (
              <p className={mismatch ? 'form-error' : 'muted small'} style={{ marginBottom: 0 }}>
                {mismatch
                  ? `Does not tally: ${variance > 0 ? 'over' : 'short'} ${inr(Math.abs(variance))} against the ${inr(expected)} last counted${denomDiff ? ` — ${denomDiff}` : ''}.`
                  : `Tallies with the ${inr(expected)} last counted.`}
              </p>
            )}
            {mismatch && (
              <div className="field" style={{ marginTop: '.3rem' }}><label>Why the difference? *</label>
                <input value={openingNote} onChange={(e) => setOpeningNote(e.target.value)}
                  placeholder="Cash banked overnight, handed to the owner, shortfall reported…" />
              </div>
            )}
            <label className="chk" style={{ marginTop: '.5rem' }}>
              <input type="checkbox" checked={empty} onChange={(e) => { setEmpty(e.target.checked); if (e.target.checked) setCounts({}); }} /> Starting with an empty drawer
            </label>
            <div className="flex gap" style={{ alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '.5rem' }}>
              <div className="field" style={{ flex: 1, minWidth: 180 }}><label>Note</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
              </div>
              <button className="btn btn-gold" onClick={open} disabled={busy || (!counted && !empty) || (mismatch && !openingNote.trim())}>
                {busy ? 'Opening…' : `Open register · ${inr(openingCash)}`}
              </button>
            </div>
            {!counted && !empty && <p className="muted small" style={{ margin: '.4rem 0 0' }}>Count the open balance before opening — or tick the empty-drawer box.</p>}
          </>
        )}
        {error && <p className="form-error">{error}</p>}
      </div>
    </div>
  );
};

export const CloseRegisterModal = ({ session, onClosed, onClose }) => {
  const [counts, setCounts] = useState({});
  const [touched, setTouched] = useState(false);
  const [countedUpi, setUpi] = useState('');
  const [countedCard, setCard] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');

  const expected = Number(session.expectedCash) || 0;
  const countedCash = countsTotal(counts);
  const variance = !touched ? null : Math.round((countedCash - expected) * 100) / 100;
  const byMode = session.byMode || {};

  const close = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/pos/session/${session.id}/close`, {
        countedCash, closingCounts: counts,
        countedUpi: countedUpi === '' ? undefined : Number(countedUpi),
        countedCard: countedCard === '' ? undefined : Number(countedCard),
        note,
      });
      toast.success('Register closed');
      onClosed(data.session); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Close register — ${session.code}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={close} disabled={busy || !touched}>{busy ? 'Closing…' : `Close register · ${inr(countedCash)}`}</button></>}>
      <div className="table-wrap">
        <table className="grid">
          <tbody>
            <tr><td>Bills</td><td className="right">{session.salesCount}</td></tr>
            <tr><td>Takings</td><td className="right">{inr(session.salesTotal)}</td></tr>
            <tr><td>Cash sales</td><td className="right">{inr(byMode.cash)}</td></tr>
            <tr><td>UPI · Card</td><td className="right">{inr(byMode.upi)} · {inr(byMode.card)}</td></tr>
            <tr><td>Open balance</td><td className="right">{inr(session.openingCash)}</td></tr>
            {Number(session.refundsCash) > 0 && <tr><td>Cash refunds</td><td className="right">−{inr(session.refundsCash)}</td></tr>}
            {Number(session.cashExpenses) > 0 && <tr><td>Cash paid out (expenses)</td><td className="right">−{inr(session.cashExpenses)}</td></tr>}
          </tbody>
          <tfoot><tr className="total-row"><td><strong>Cash expected in drawer</strong></td><td className="right"><strong>{inr(expected)}</strong></td></tr></tfoot>
        </table>
      </div>

      <div style={{ marginTop: '.7rem' }}>
        <CashCount counts={counts} onChange={(c) => { setCounts(c); setTouched(true); }} compact label="Cash counted — note by note" />
      </div>
      <div className="row" style={{ marginTop: '.5rem' }}>
        <div className="field" style={{ maxWidth: 170 }}><label>UPI settled (₹)</label><input type="number" min="0" value={countedUpi} onChange={(e) => setUpi(e.target.value)} placeholder="optional" /></div>
        <div className="field" style={{ maxWidth: 170 }}><label>Card settled (₹)</label><input type="number" min="0" value={countedCard} onChange={(e) => setCard(e.target.value)} placeholder="optional" /></div>
      </div>
      {variance != null && (
        <p className={Math.abs(variance) < 0.5 ? 'muted small' : 'form-error'}>
          {Math.abs(variance) < 0.5 ? 'Drawer matches the expected cash.' : variance > 0 ? `Over by ${inr(variance)} — more cash than the bills account for.` : `Short by ${inr(-variance)}.`}
        </p>
      )}
      <div className="field"><label>Note</label><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain a difference, hand-over detail…" /></div>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// The register's standing at a glance, floating above the bottom of the till so
// it stays in view while the cashier works down the cart. Closing the register is
// the drawer control in the topbar — this bar only reports.
export const RegisterBar = ({ session, onCloseRegister }) => {
  const byMode = session.byMode || {};
  const item = (k, v, cls) => (
    <span className={`register-bar-item${cls ? ` ${cls}` : ''}`}><span className="k">{k}</span>{v}</span>
  );
  return (
    <div className="register-bar no-print">
      <span className="badge badge-green">Register open</span>
      <span className="chip-static">{session.salesCount} bill{session.salesCount === 1 ? '' : 's'}</span>
      <span className="register-bar-code">{session.code}</span>
      <span className="register-bar-figures">
        {item('Open balance', inr(session.openingCash))}
        {item('Cash', inr(byMode.cash))}
        {item('UPI', inr(byMode.upi))}
        {item('Card', inr(byMode.card))}
        {Number(session.refundsCash) > 0 && item('Refunds', `−${inr(session.refundsCash)}`)}
        {Number(session.cashExpenses) > 0 && item('Paid out', `−${inr(session.cashExpenses)}`)}
        {item('Cash in drawer', inr(session.expectedCash), 'drawer')}
        <button className="btn btn-outline btn-sm" onClick={onCloseRegister}>Close register</button>
      </span>
    </div>
  );
};
